import { useCallback, useEffect, useState } from 'react';
import { BellRing, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { getAlertRules, updateAlertRule, type AlertRule, type AlertRulePatch } from '@/lib/adminApi';
import { useRBAC } from '@/contexts/RBACContext';
import { toast } from 'react-toastify';
import { AlertNumberKnob as NumberKnob } from './AlertNumberKnob';
import { CriticalBatteryFollowUps } from './CriticalBatteryFollowUps';

/**
 * Systems/telematics alerts.
 *
 * Exists because on 2026-08-11 the Sol-Ark sync failed 79 times over 7 hours
 * and nothing said so. Rules are seeded server-side from a code catalogue; this
 * panel only edits the fields a human owns — on/off, how many failures to
 * tolerate, and where to send it.
 *
 * The "Critical battery on a trailer" rule has two lines instead (Eric: critical
 * below 30%, warning below 50%). They set the board's battery colours even while
 * its notices are off, so they stay editable then. While its notices are on it
 * also has two follow-ups (CriticalBatteryFollowUps): repeat while nobody owns
 * the alert, and alert again below a lower level.
 *
 * The "Low runtime on a trailer" rule (Eric 2026-11-01) has one box, warn under N
 * hours of power left (1–12), and no channels: it shows on the board and sends nothing.
 */
/** The pre-optimistic value for just the fields we touched. */
function revertTo(rule: AlertRule, optimistic: Partial<AlertRule>): Partial<AlertRule> {
  const reverted: Record<string, unknown> = {};
  for (const key of Object.keys(optimistic) as Array<keyof AlertRule>) reverted[key] = rule[key];
  return reverted as Partial<AlertRule>;
}

const THRESHOLD_LABELS: Record<string, string> = {
  threshold: 'Failures before alerting',
  minutes: 'Minutes before stale',
  percent: 'Critical below (%)',
  hours: 'Warn under (hours)',
};

/** The allowed range for each kind of number box (the server checks the same). */
const THRESHOLD_RANGES: Record<string, { min: number; max?: number }> = {
  percent: { min: 5, max: 90 },
  hours: { min: 1, max: 12 },
};

/** Low runtime shows on the board only and sends nothing, so it has no channels. */
function sendsNotices(rule: AlertRule): boolean {
  return rule.conditionType !== 'unit_low_runtime';
}

const CHANNELS: Array<{ key: string; label: string }> = [
  { key: 'email', label: 'Email' },
  { key: 'in_app', label: 'In-app' },
];

export function SystemAlertsPanel() {
  const { isSuperadmin } = useRBAC();
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // A set, not a single key: two rules can be in flight at once and a single
  // key disabled the wrong controls and lost the other's result.
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  // Bumped on rollback to remount uncontrolled threshold inputs — React ignores
  // defaultValue on re-render, so a rejected value would otherwise stay onscreen.
  const [ruleVersion, setRuleVersion] = useState(0);

  const fetchRules = useCallback(async () => {
    if (!isSuperadmin) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await getAlertRules();
      setRules(Array.isArray(data.rules) ? data.rules : []);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to fetch alert rules:', error);
      setLoadError(error instanceof Error ? error.message : 'Could not load alert rules');
    } finally {
      setIsLoading(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const patchRule = async (
    key: string,
    patch: AlertRulePatch,
    optimistic: Partial<AlertRule>,
  ) => {
    // Functional updates throughout: capturing `rules` from the closure meant a
    // second rule's save, started before the first resolved, rolled back onto a
    // stale snapshot and silently discarded the first rule's committed change.
    // The rule as it was BEFORE this change, for the revert. (Its controls are
    // disabled while it saves, so the same rule can't have two saves in flight.)
    const before = rules.find(r => r.key === key);
    setRules(current => current.map(r => (r.key === key ? { ...r, ...optimistic } : r)));
    setSavingKeys(current => new Set(current).add(key));
    try {
      const data = await updateAlertRule(key, patch);
      setRules(current => current.map(r => (r.key === key ? data.rule : r)));
    } catch (error) {
      // Revert only THIS rule's touched fields, leaving concurrent edits to others intact.
      setRules(current => current.map(r => (r.key === key && before ? { ...r, ...revertTo(before, optimistic) } : r)));
      setRuleVersion(v => v + 1);
      toast.error(error instanceof Error ? error.message : 'Save failed');
    } finally {
      setSavingKeys(current => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  };

  const toggleChannel = (rule: AlertRule, channel: string, checked: boolean) => {
    const next = checked
      ? CHANNELS.filter(c => rule.channels.includes(c.key) || c.key === channel).map(c => c.key)
      : rule.channels.filter(c => c !== channel);
    // A rule with no channel is enabled but mute — the server rejects it too.
    if (next.length === 0) return;
    patchRule(rule.key, { channels: next }, { channels: next });
  };

  if (!isSuperadmin) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
        <BellRing className="w-5 h-5 text-[#8FB560]" />
        System Alerts
        {savingKeys.size > 0 && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h2>
      <p className="text-xs text-muted-foreground mb-6">
        Notifies once when a condition starts and once when it clears — not on every check.
      </p>

      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : loadError ? (
        <div role="alert" className="text-xs text-destructive">
          Could not load alert rules: {loadError}
        </div>
      ) : rules.length === 0 ? (
        <div className="text-xs text-muted-foreground">No alert rules configured.</div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <div key={rule.key} className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-white">{rule.label}</h3>
                    {rule.state === 'alerting' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E0556C]/15 text-[#E0556C]">
                        Firing
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{rule.description}</p>
                </div>
                <Switch
                  checked={rule.enabled}
                  disabled={savingKeys.has(rule.key)}
                  onCheckedChange={checked =>
                    patchRule(rule.key, { enabled: checked }, { enabled: checked })
                  }
                />
              </div>

              {(rule.enabled || rule.thresholdField === 'percent') && (
                <div className="flex flex-wrap items-center gap-6 mt-4">
                  {rule.thresholdField && (
                    <NumberKnob
                      label={THRESHOLD_LABELS[rule.thresholdField]}
                      inputKey={`${rule.key}-${rule.threshold}-${ruleVersion}`}
                      value={rule.threshold ?? 1}
                      min={THRESHOLD_RANGES[rule.thresholdField]?.min ?? 1}
                      max={THRESHOLD_RANGES[rule.thresholdField]?.max}
                      disabled={savingKeys.has(rule.key)}
                      onSave={value => patchRule(rule.key, { threshold: value }, { threshold: value })}
                    />
                  )}
                  {rule.thresholdField === 'percent' && (
                    <NumberKnob
                      label="Warning below (%)"
                      inputKey={`${rule.key}-warning-${rule.warningThreshold}-${ruleVersion}`}
                      value={rule.warningThreshold ?? 50}
                      min={(rule.threshold ?? 30) + 1}
                      max={95}
                      disabled={savingKeys.has(rule.key)}
                      onSave={value => patchRule(rule.key, { warningThreshold: value }, { warningThreshold: value })}
                    />
                  )}

                  {rule.enabled && sendsNotices(rule) && (
                  <div className="flex items-center gap-4">
                    {CHANNELS.map(channel => {
                      const checked = rule.channels.includes(channel.key);
                      const isLast = checked && rule.channels.length === 1;
                      return (
                        <label
                          key={channel.key}
                          className="flex items-center gap-2 text-xs text-muted-foreground"
                        >
                          <Checkbox
                            checked={checked}
                            disabled={savingKeys.has(rule.key) || isLast}
                            onCheckedChange={value => toggleChannel(rule, channel.key, value === true)}
                            className="data-[state=checked]:bg-[#8FB560] data-[state=checked]:border-[#8FB560]"
                          />
                          {channel.label}
                        </label>
                      );
                    })}
                  </div>
                  )}

                  {rule.enabled && rule.thresholdField === 'percent' && (
                    <CriticalBatteryFollowUps
                      rule={rule}
                      saving={savingKeys.has(rule.key)}
                      version={ruleVersion}
                      onPatch={patch => patchRule(rule.key, patch, patch)}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
